import * as React from 'react'
import { useForm } from '@tanstack/react-form'
import { Minus, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { sheetContentClass, sheetFooterClass, type SheetLayout } from '@/components/shared/sheet-layout'
import { Input } from '@/components/ui/input'
import { DurationInput } from '@/components/shared/duration-input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { InputGroup } from '@/components/ui/input-group'
import { BaristaCartCountFields } from '@/components/shared/barista-cart-count-fields'
import { TimezoneSelect } from '@/components/shared/timezone-select'
import { LocationTimezoneSync } from '@/components/shared/location-timezone-sync'
import { useResolveLocationTimezone } from '@/stores/events.store'
import type { QuoteEventFormValue } from './quote-event-mapping'

interface QuoteEventSheetBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  defaultEventLength?: number
  defaultEventGuests?: number
  defaultEventTimezone?: string
  defaultEventLocation?: string
  defaultBaristaCount?: number
  defaultCartCount?: number
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

interface QuoteEventSheetCreateProps extends QuoteEventSheetBaseProps {
  mode: 'create'
  onSave: (event: Omit<QuoteEventFormValue, 'tempId'>) => void
  event?: never
  eventIndex?: never
}

interface QuoteEventSheetEditProps extends QuoteEventSheetBaseProps {
  mode: 'edit'
  event: QuoteEventFormValue
  eventIndex: number
  onSave: (event: QuoteEventFormValue) => void
}

type QuoteEventSheetProps = QuoteEventSheetCreateProps | QuoteEventSheetEditProps

/**
 * QuoteEventSheet - Sheet component for creating or editing quote events
 *
 * Features:
 * - Enter event date, time, duration (hours), guest count, and location
 * - Uses TanStack Form for form state management
 * - Works with local form state (not API calls)
 * - Supports both create and edit modes
 *
 * @param mode - 'create' or 'edit'
 * @param event - The event to edit (required for edit mode)
 * @param eventIndex - The index of the event (for display as Event #, required for edit mode)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 * @param onSave - Callback when event is saved
 */
export function QuoteEventSheet({
  mode,
  event,
  eventIndex,
  open,
  onOpenChange,
  onSave,
  disabled = false,
  defaultEventLength,
  defaultEventGuests,
  defaultEventTimezone,
  defaultEventLocation,
  defaultBaristaCount,
  defaultCartCount,
  layout,
}: QuoteEventSheetProps) {
  const { resolve: resolveTimezoneFromLocation } = useResolveLocationTimezone()

  // Extract time from date for the time picker
  const getInitialTime = (date?: Date): string => {
    if (!date) return ''
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }

  const [eventTime, setEventTime] = React.useState<string>(() => {
    if (mode === 'edit' && event?.eventDate) {
      return getInitialTime(event.eventDate)
    }
    return ''
  })

  // Helper to combine date and time into a single Date object
  const combineDateTime = (date: Date | undefined, time: string): Date | undefined => {
    if (!date) return undefined

    const newDate = new Date(date)
    if (time) {
      const [hours, minutes] = time.split(':').map(Number)
      newDate.setHours(hours, minutes, 0, 0)
    }
    return newDate
  }

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      eventDate: mode === 'edit' ? event?.eventDate : undefined,
      eventLength: mode === 'edit' ? (event?.eventLength ?? 0) : (defaultEventLength ?? 0),
      eventGuests: mode === 'edit' ? (event?.eventGuests ?? 0) : (defaultEventGuests ?? 0),
      eventTimezone: mode === 'edit' ? (event?.eventTimezone ?? '') : (defaultEventTimezone ?? ''),
      eventLocation: mode === 'edit' ? (event?.eventLocation ?? '') : (defaultEventLocation ?? ''),
      baristaCount: mode === 'edit' ? (event?.baristaCount ?? 1) : (defaultBaristaCount ?? 1),
      cartCount: mode === 'edit' ? (event?.cartCount ?? 1) : (defaultCartCount ?? 1),
    },
    onSubmit: async ({ value }) => {
      if (mode === 'create') {
        onSave({
          eventDate: value.eventDate,
          eventLength: value.eventLength || undefined,
          eventGuests: value.eventGuests || undefined,
          eventTimezone: value.eventTimezone || undefined,
          eventLocation: value.eventLocation || undefined,
          baristaCount: value.baristaCount,
          cartCount: value.cartCount,
        })
      } else {
        onSave({
          ...event,
          eventDate: value.eventDate,
          eventLength: value.eventLength || undefined,
          eventGuests: value.eventGuests || undefined,
          eventTimezone: value.eventTimezone || undefined,
          eventLocation: value.eventLocation || undefined,
          baristaCount: value.baristaCount,
          cartCount: value.cartCount,
        })
      }
      form.reset()
      setEventTime('')
    },
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
      setEventTime('')
    } else if (mode === 'edit' && event) {
      // Reset form to event values when opening in edit mode
      form.reset({
        eventDate: event.eventDate,
        eventLength: event.eventLength ?? 0,
        eventGuests: event.eventGuests ?? 0,
        eventTimezone: event.eventTimezone ?? '',
        eventLocation: event.eventLocation ?? '',
        baristaCount: event.baristaCount ?? 1,
        cartCount: event.cartCount ?? 1,
      })
      setEventTime(getInitialTime(event.eventDate))
    }
    onOpenChange(newOpen)
  }

  const title = mode === 'create' ? 'Add Event' : `Edit Event #${(eventIndex ?? 0) + 1}`
  const description = mode === 'create'
    ? 'Enter the event details. Click add when you\'re done.'
    : 'Update the event details. Click save when you\'re done.'
  const submitText = mode === 'create' ? 'Add Event' : 'Save Changes'
  const formId = mode === 'create' ? 'quote-event-create-form' : 'quote-event-edit-form'

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className={sheetContentClass(layout)}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {description}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id={formId}
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
            {/* Event Date */}
            <form.Field name="eventDate">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-date">Event Date</Label>
                  <DatePicker
                    value={field.state.value}
                    onChange={(date) => {
                      const combined = combineDateTime(date, eventTime)
                      field.handleChange(combined)
                    }}
                    disabled={disabled}
                  />
                </div>
              )}
            </form.Field>

            {/* Event Time */}
            <div className="grid gap-3">
              <Label htmlFor="event-time">Event Time</Label>
              <TimePicker
                value={eventTime}
                onChange={(time) => {
                  setEventTime(time)
                  const combined = combineDateTime(form.state.values.eventDate, time)
                  form.setFieldValue('eventDate', combined)
                }}
                disabled={disabled}
                placeholder="Select time"
              />
            </div>

            {/* Timezone */}
            <form.Subscribe selector={(s) => s.values.eventLocation}>
              {(eventLocation) => (
                <form.Field name="eventTimezone">
                  {(field) => {
                    const trimmedLocation = (eventLocation ?? '').trim()
                    return (
                      <div className="grid gap-1">
                        <TimezoneSelect
                          id="quote-event-timezone"
                          label="Timezone"
                          value={field.state.value || undefined}
                          onChange={(value) => field.handleChange(value)}
                          disabled={disabled}
                          onResolveFromLocation={() => resolveTimezoneFromLocation(trimmedLocation)}
                          resolveDisabledReason={trimmedLocation ? undefined : 'Set an event location first'}
                        />
                        <p className="text-xs text-muted-foreground">
                          Follows the event location automatically. Pick a zone here to override it.
                        </p>
                      </div>
                    )
                  }}
                </form.Field>
              )}
            </form.Subscribe>

            {/* Event Duration */}
            <form.Field name="eventLength">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-length">Duration</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <DurationInput
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={disabled}
                      min={0}
                      step={0.25}
                    />
                  </div>
                </div>
              )}
            </form.Field>

            {/* Guest Count */}
            <form.Field name="eventGuests">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-guests">Guest Count</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-r border-input"
                      onClick={() => {
                        const newValue = Math.max(0, field.state.value - 25)
                        field.handleChange(newValue)
                      }}
                      disabled={disabled || field.state.value <= 0}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease by 25</span>
                    </Button>
                    <Input
                      id="event-guests"
                      type="number"
                      min="0"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const value = Number(e.target.value) || 0
                        const clampedValue = Math.max(0, value)
                        field.handleChange(clampedValue)
                        field.handleBlur()
                      }}
                      disabled={disabled}
                      placeholder="0"
                      className="border-0 rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-l border-input"
                      onClick={() => {
                        const newValue = field.state.value + 25
                        field.handleChange(newValue)
                      }}
                      disabled={disabled}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase by 25</span>
                    </Button>
                  </div>
                </div>
              )}
            </form.Field>

            {/* Barista & Cart Count */}
            <form.Field name="baristaCount">
              {(baristaField) => (
                <form.Field name="cartCount">
                  {(cartField) => (
                    <BaristaCartCountFields
                      baristaCount={baristaField.state.value}
                      cartCount={cartField.state.value}
                      onBaristaCountChange={baristaField.handleChange}
                      onCartCountChange={cartField.handleChange}
                      onBaristaCountBlur={baristaField.handleBlur}
                      onCartCountBlur={cartField.handleBlur}
                      disabled={disabled}
                    />
                  )}
                </form.Field>
              )}
            </form.Field>

            {/* Event Location */}
            <form.Field name="eventLocation">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-location">Location</Label>
                  <InputGroup>
                    <PlacesAutocomplete
                      id="event-location"
                      value={field.state.value || ''}
                      onChange={(value) => field.handleChange(value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter event location"
                      disabled={disabled}
                    />
                  </InputGroup>
                  <form.Subscribe selector={(s) => s.values.eventTimezone}>
                    {(eventTimezone) => (
                      <LocationTimezoneSync
                        location={field.state.value ?? ''}
                        timezone={eventTimezone ?? ''}
                        onTimezoneChange={(timezone) => form.setFieldValue('eventTimezone', timezone)}
                        resolve={resolveTimezoneFromLocation}
                      />
                    )}
                  </form.Subscribe>
                </div>
              )}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter className={sheetFooterClass(layout)}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={disabled}>
              Cancel
            </Button>
          </SheetClose>
          <Button type="submit" form={formId} disabled={disabled}>
            {submitText}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
